/**
 * @typedef {import('@azure/msal-browser').RedirectRequest} RedirectRequest
 */

import * as microsoftTeams from "@microsoft/teams-js";
import * as msal from '@azure/msal-browser';
import axios from 'axios';
import getEnvironment from 'lib/env';
import moment from 'moment';
import store from '../index';
import DateTime from '_class/DateTime';
import { appInsights } from 'lib/appInsights';
import { v4 as uuidv4 } from 'uuid';

/**
 * @typedef {Object} MSALConfig MSAL Config model
 */
const msalConfig = {
	auth: {
		clientId: getEnvironment().clientId,
		authority: 'https://login.microsoftonline.com/common/',
		postLogoutRedirectUri: window.location.origin + '/',
		redirectUri: window.location.origin + '/',
		navigateToLoginRequestUrl: false,
	},
	cache: {
		// TODO: Decide if we should use sessionStorage (default) or localStorage here
		// localStorage seems to work better for our use case
		cacheLocation: "localStorage",
		storeAuthStateInCookie: true,
	},
};

console.log('_env', getEnvironment());

/**
 * @type {RedirectRequest} loginRequest
 */
const loginRequest = {
	scopes: [
		"openid",
		"profile",
		"offline_access",
	],
};

/**
 * @type {Array} graphScopes
 */
const graphScopes = [
	"Files.ReadWrite.All",
];

const instance = new msal.PublicClientApplication(msalConfig);
(async () => {
	await instance.initialize();
})();

const _addToast = (message, type = 'warning') => {
	const error = {
		translateMsg: message,
		type: type,
		target: 'API',
		time: new DateTime(),
	};

	store.dispatch({
		type: 'ADD_ERROR',
		payload: error,
	});
}

const requirePopupConsent = (clientId, redirectUri, scopes, knownClientApplications = []) => new Promise(async (resolve, reject) => {
	const msalCustomConfig = {
		auth: {
			clientId: clientId,
			authority: 'https://login.microsoftonline.com/common/',
			postLogoutRedirectUri: window.location.origin + '/',
			redirectUri: window.location.origin + redirectUri,
			navigateToLoginRequestUrl: false,
		},
		cache: {
			// TODO: Decide if we should use sessionStorage (default) or localStorage here
			// localStorage seems to work better for our use case
			cacheLocation: "localStorage",
			storeAuthStateInCookie: false,
		},
	};

	const msalClient = new msal.PublicClientApplication(msalCustomConfig);
	await msalClient.initialize();  // krävs från och med v3

	var customRequest = {
		scopes: scopes
	}

	customRequest.account = getUser();

	msalClient.acquireTokenSilent(customRequest).then(response => {
		resolve(response);
	}).catch(error => {
		msalClient.acquireTokenPopup(customRequest).then((token) => {
			resolve(token);
		}).catch(error => {
			reject(error);
		});
	});
});

const getToken = (request) => new Promise(async (resolve, reject) => {
	request.account = getUser();

	if (microsoftTeams.app.isInitialized()) {
		microsoftTeams.authentication.getAuthToken().then((token) => {
			resolve({ accessToken: token });
		}).catch((error) => {
			appInsights.trackTrace({ message: "EDU005 | Teams SDK - getAuthToken fails" }, error);
			console.log(error);
			reject('Failed to retrieve token from Teams SDK');
		});
	} else {
		if (instance.getAllAccounts().length == 0) {
			reject('User not logged in');
			return true;
		}

		const token = await new Promise(async (resolveToken, rejectToken) => {
			instance.acquireTokenSilent(request).then(token => {
				resolveToken(token);
			})
				.catch(async (error) => {
					appInsights.trackTrace({ message: "EDU005 | MSAL - AcquireTokenSilent fails (step 503)" }, error);
					resolveToken(null);
				});
		}).catch(error => {
			console.log("error catch", error);
			reject(error);
		});

		resolve(token);
	}
});

let tokenErrorCallbacks = [];
const onMsalTokenError = (callback, ...args) => {
	if (callback != null) {
		tokenErrorCallbacks.push(callback);
		return true;
	}

	tokenErrorCallbacks.forEach(callback => {
		if (callback != null && typeof callback == 'function') {
			callback(...args);
		}
	})
}

let loginRequiredCallbacks = [];
const onMsalLoginRequired = (callback) => {
	return new Promise((resolve) => {
		if (callback != null) {
			loginRequiredCallbacks.push(callback);
			resolve(true);
			return true;
		}

		let promises = [];

		loginRequiredCallbacks.forEach(callback => {
			if (callback != null) {
				let promise = callback();
				promises.push(promise);

				return true;
			}
		})

		Promise.all(promises).then(() => {
			resolve(1);
		})
	})
}

const getUser = () => {
	if (microsoftTeams.app.isInitialized()) {
		return { username: localStorage.getItem('session.username') };
	} else {
		const accounts = instance.getAllAccounts();
		if (accounts.length > 0) {
			const username = localStorage.getItem('session.username');
			if (username) {
				return accounts.find(account => account.username === username) || accounts[0];
			} else {
				return accounts[0];
			}
		}
	}

	return null;
}

const setUser = (username) => {
	localStorage.setItem('session.username', username);
}

const setOnboardingUser = (username) => {
	var existingUserName = localStorage.getItem('onboarding.username');

	if (username != existingUserName) {
		localStorage.removeItem('onboarding.expireTokenIn');
		localStorage.setItem('onboarding.username', username);
	}
}

const setOnboardingToken = (token) => {
	localStorage.setItem('onboarding.token', token);
}

const setOnboardingExpireTokenIn = (expireIn) => {
	localStorage.setItem('onboarding.expireTokenIn', moment().add(expireIn, 's').format("YYYY-MM-DD HH:mm"))
}

const isTokenValid = () => {
	var date = localStorage.getItem('onboarding.expireTokenIn');

	if (date != null) {
		var now = moment();
		var expireDate = moment(date);

		if (now < expireDate) {
			return true;
		}
	}

	return false;
}

export const getSchoolId = async () => {
	var currentStore = store.getState();

	// First we check store, this will be the most used scenario and active school should reside in store
	var currentActiveSchoolId = currentStore?.user?.activeSchool;
	if (currentActiveSchoolId != null && Number.isInteger(Number(currentActiveSchoolId))) {
		return Number(currentActiveSchoolId);
	}

	// Get the user id either if user in teams context or browser context. 
	const userId = await getCurrentUserId();
	if (userId == null) {
		return null;
	}

	// Then we try to get the school from Localstorage
	currentActiveSchoolId = localStorage.getItem('hal.school' + userId);
	if (currentActiveSchoolId != null && Number.isInteger(Number(currentActiveSchoolId))) {
		store.dispatch({ type: "SET_SCHOOL", payload: currentActiveSchoolId });
		return Number(currentActiveSchoolId);
	}
	// Last check if user is in store, then get the first school if any schools
	currentActiveSchoolId = currentStore?.user?.currentUser?.schools?.[0]?.id;
	if (currentActiveSchoolId != null && Number.isInteger(Number(currentActiveSchoolId))) {
		store.dispatch({ type: "SET_SCHOOL", payload: currentActiveSchoolId });
		return Number(currentActiveSchoolId);
	}
	return null;
};

const getCurrentUserId = async () => {
	if (typeof microsoftTeams !== "undefined" && typeof microsoftTeams.app !== "undefined") {
		if (!microsoftTeams.app.isInitialized()) {
			try {
				await microsoftTeams.app.initialize();
			} catch (error) {
				// Trying to get user id from MSAL as fallback if Teams SDK initialization fails, this can happen if user is not in Teams but microsoftTeams library is still loaded for some reason (should not be the case in production, but can happen in development)
				const accounts = instance.getAllAccounts();
				if (accounts.length > 0) {
					const account = getUser();
					if (account?.homeAccountId) {
						return account.homeAccountId.split('.')[0];
					}
				}
				appInsights.trackTrace({ message: "EDU025a | Teams SDK - Initialization failed" }, error);
				return null;
			}
		}
		try {
			const context = await microsoftTeams.app.getContext();
			return context.user?.id || null;
		} catch (error) {
			appInsights.trackTrace({ message: "EDU025ab | Teams SDK - Could not fetch user id" }, error);
			return null;
		}
	}

	const accounts = instance.getAllAccounts();
	if (accounts.length > 0) {
		const account = getUser();
		if (account?.homeAccountId) {
			return account.homeAccountId.split('.')[0];
		}
		else {
			appInsights.trackTrace({ message: "EDU025ac | Could not fetch user id" });
		}
	}
	return null;
};


const getUserId = () => {
	const user = getUser();

	if (user != null) {
		if (user.homeAccountId?.indexOf('.') > -1) {
			return user.homeAccountId.split('.')[0];
		}

		return user.homeAccountId;
	}

	return null;
}

const getTokenForScope = async () => {
	try {
		const account = getUser();
		const customRequest = {
			scopes: ["Files.ReadWrite.All"],
			account: account,
		};

		try {
			const tokenResponse = await instance.acquireTokenSilent(customRequest);
			return tokenResponse.accessToken;
		} catch (silentError) {
			const tokenResponse = await instance.acquireTokenPopup(customRequest);
			return tokenResponse.accessToken;
		}
	} catch (error) {
		console.error("Token acquisition error:", error);
		return null;
	}
};

const getConsentStatus = async () => {
	const account = getUser();
	instance.acquireTokenSilent({
		scopes: ["Files.ReadWrite.All"],
		account: account
	}).then((tokenResponse) => {
		if (!tokenResponse.scopes.includes("Files.ReadWrite.All")) {
			appInsights.trackTrace({ message: "EDU100 | MSAL - User in tenant " + tokenResponse.tenantId + " has not consented to Files.ReadWrite.All" });
		}
		else {
			appInsights.trackTrace({ message: "EDU101 | MSAL - User in tenant " + tokenResponse.tenantId + " has consented to Files.ReadWrite.All" });
		}
	}).catch((error) => {
		appInsights.trackTrace({ message: "EDU102 | MSAL - User in tenant " + account.tenantId + " errorcode " + error.errorCode });
	});

};

const http = axios.create({
	baseURL: getEnvironment().baseURL,
});

const errors = {
	AuthError: msal.AuthError,
	AuthErrorMessage: msal.AuthErrorMessage,
	BrowserAuthError: msal.BrowserAuthError,
	BrowserAuthErrorMessage: msal.BrowserAuthErrorMessage,
	BrowserConfigurationAuthError: msal.BrowserConfigurationAuthError,
	BrowserConfigurationAuthErrorMessage: msal.BrowserConfigurationAuthErrorMessage,
	InteractionRequiredAuthError: msal.InteractionRequiredAuthError,
};

const clearInteractionProgress = () => {
	if (document.cookie.indexOf('interaction_in_progress') > -1 || document.cookie.indexOf('interaction.status') > -1) {
		document.cookie.split(";").forEach(function (c) {
			document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
		});
	}

	Object.keys(sessionStorage).forEach(key => {
		if (key.indexOf('interaction.status') > -1) {
			sessionStorage.removeItem(key);
		}
	});

	Object.keys(localStorage).forEach(key => {
		if (key.indexOf('interaction.status') > -1) {
			localStorage.removeItem(key);
		}
	});
}

http.interceptors.request.use((config) => {
	return new Promise(async (resolve, reject) => {
		const excludedPaths = ['tenants/educationbackendbaseurl', 'tenants', 'users/me'];
		let request = { ...loginRequest };
		if (config.url.indexOf('graph.microsoft.com') > -1) {
			// Add scopes for MS Graph
			request.scopes = [
				...graphScopes,
				...request.scopes
			];
		} else {
			// Add scopes for environment
			request.scopes = [
				...getEnvironment().scopes,
				...request.scopes
			];
		}

		var token = null;
		var onboardingInit = localStorage.getItem('onboarding:init');

		/** If Admin consent is present then we make sure accessToken always have to taken from getToken method **/
		var onboardingValid = localStorage.getItem("onboarding.valid");

		const { pathname } = window.location;

		if ((pathname.indexOf('/teams/he') > -1 && pathname.indexOf('/tab-configure') < 0 && onboardingValid == null) || (pathname.indexOf('/teams/he/tab-configure') > -1 && onboardingInit != null && onboardingValid == null)) {
			let accessToken = localStorage.getItem('onboarding.token');

			if (!accessToken)
				console.log("token does not exit in localstorage", accessToken);

			token = {
				accessToken: accessToken
			}
		} else {
			token = await getToken(request);
		}

		const lcid = localStorage.getItem('language');
		let currentSchool = await getSchoolId();

		if (currentSchool != null) {
			if (config.params == null) {
				config.params = {};
			}
			config.params['SchoolId'] = currentSchool;
		}
		else if (!excludedPaths.some(path => config.url?.toLowerCase() == path) && !config.url?.toLowerCase().startsWith('status')) {
			appInsights.trackTrace({
				message: "EDU025b | MSAL - SchoolId could not be found",
				properties: { url: config.url }
			});
		}

		if (lcid != null) {
			if (config.params == null) {
				config.params = {};
			}

			config.params['lcid'] = lcid;
		}

		if (token != null) {
			config.headers.common['Authorization'] = "Bearer " + token.accessToken;
		}

		const method = config.method?.toLowerCase();
		if (method === 'post' || method === 'patch' || method === 'put') {
			if (!config.headers['X-Idempotency-Key']) {
				config.headers['X-Idempotency-Key'] = uuidv4();
			}
		}

		// TODO: Reject promise if something goes wrong with fetching token
		resolve(config);
	})
}, function (error) {
	return Promise.reject(error);
});

http.interceptors.response.use((response) => {
	if (response.status == 204) {
		response.data = null;
	}

	return response;
}, async (error) => {

	if (error.config && error.config._isPing) {
		return Promise.reject(error);
	}

	// We try to handle result code 0
	const isNetworkError = error.message === 'Network Error' || (error.response && error.response.status === 0);

	if (isNetworkError) {
		appInsights.trackTrace({ message: "EDU020 | MSAL lib interceptor got status 0 (Network Error)" }, {
			data: error?.response?.data,
			params: error?.config?.params,
			url: error?.config?.url,
			method: error?.config?.method,
			baseURL: error?.config?.baseURL,
			schoolId: error?.config?.params ? error.config.params['SchoolId'] : null,
		});

		// EDU030 / EDU031 — Detailed logging for failed POST/PUT/PATCH on Assessment and Planning save operations.
		// These tags are used for alerting in Azure Monitor — do not change the tag identifiers.
		const failedMethod = error.config?.method?.toLowerCase();
		const failedUrl = error.config?.url ?? '';

		if (['post', 'put', 'patch'].includes(failedMethod)) {
			// Strip the Authorization header to avoid logging bearer tokens.
			const safeHeaders = { ...error.config?.headers };
			delete safeHeaders['Authorization'];
			delete safeHeaders['common'];

			const sharedProps = {
				url: failedUrl,
				method: failedMethod,
				schoolId: error.config?.params?.SchoolId ?? null,
				retryCount: error.config?.headers?.Retries ?? '0',
				headers: JSON.stringify(safeHeaders),
				payload: error.config?.data ?? null,
			};

			if (failedUrl.includes('assessments')) {
				appInsights.trackTrace(
					{ message: "POST_FAILURE_SAVE_ASSESSMENT | Failed POST on assessments endpoint (status 0)" },
					sharedProps
				);
			} else if (failedUrl.includes('planning')) {
				appInsights.trackTrace(
					{ message: "POST_FAILURE_SAVE_PLANNING | Failed POST on planning endpoint (status 0)" },
					sharedProps
				);
			}
		}

		const max_retries = 2;

		if (error.config.headers['Retries'] == null) {
			error.config.headers['Retries'] = '0';
		}

		const currentRetries = parseInt(error.config.headers['Retries']);
		if (currentRetries == 1) {
			_addToast("Tappade anslutningen till servern. Försöker återansluta...", 'warning');
		}
		if (currentRetries >= max_retries) {
			return Promise.reject(error);
		}

		// Exponential backoff, nothing fancier than we wait 1 sek, then 2 sek on second try ( or more if we allow more than 2 tries )
		const backoffTime = (currentRetries + 1) * 2000;
		await new Promise((resolve) => setTimeout(resolve, backoffTime));

		const method = error.config.method?.toLowerCase();
		if (['post', 'put', 'patch'].includes(method)) {
			try {
				await http.get('/users/me?ping=true', { _isPing: true });
			} catch (pingError) {
				// We ignore until catch since it will trigger again if this wont fix it
				// probably due to that Internet is down and we are screwed
			}
		}

		const retryConfig = {
			...error.config,
			headers: {
				...error.config.headers,
				'Retries': (currentRetries + 1).toString()
			}
		};

		return http(retryConfig);
	}

	if (error.response && error.response.status == 401) {
		return onMsalLoginRequired().then((result) => {
			return http.request(error.config)
		})
	}

	if (error.response && error.response.status == 403) {
		appInsights.trackTrace({ message: "EDU021 | MSAL lib interceptor got status 403" }, {
			data: error?.response?.data,
			params: error?.config?.params,
			url: error?.config?.url,
			method: error?.config?.method,
			baseURL: error?.config?.baseURL,
			schoolId: error?.config?.params['SchoolId'],
		});

		// Dispatch an error (toast bar)
		_addToast("You don't seem to have permission to perform this action. For support, contact your school administrator.", 'error');

		return Promise.reject(error);
	}
	return Promise.reject(error)
});

// Export members of msal.js
export {
	loginRequest,
	getToken,
	getTokenForScope,
	getConsentStatus,
	http,
	getUser,
	setUser,
	setOnboardingUser,
	getUserId,
	setOnboardingToken,
	requirePopupConsent,
	errors,
	onMsalTokenError,
	setOnboardingExpireTokenIn,
	isTokenValid,
	onMsalLoginRequired,
	clearInteractionProgress,
};

// Export MSAL instance
export default instance;